import React, { useEffect } from 'react';
import { ElementValue } from '../../types/form-values';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { SummaryRowTemplate } from '../../helpers/templates';
import { TextareaField } from '@flos/react-ui';
import { Textarea as TextareaType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import useAppState from '../../state/appState';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getValue } from '../values/Get';
import { getMinMaxLength } from '../../helpers/getMinMaxLength';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Textarea = ({ elementId, caption, placeholder, helpText }: TextareaType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Textarea.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');
    const [fieldHasHadFocusAtLeastOnce, setFieldHasHadFocusAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }
        if (!fieldHasHadFocusAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Textarea.isValidField(formConfig, formValues, elementId, fieldHasHadFocusAtLeastOnce);
    const lengthObject = getMinMaxLength(formConfig, elementId);
    const { minLength, maxLength } = lengthObject;

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <TextareaField
                value={value}
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const value = event.target.value;
                    setFormValue(elementId, value);
                }}
                onFocus={() => {
                    setFieldHasHadFocusAtLeastOnce(true);
                }}
                onBlur={() => {
                    doValidation();
                }}
                isValid={isValid}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                label={placeholder}
                helpText={helpText}
                required={isRequiredField}
                aria-label={caption}
                data-testid="textarea"
                minLength={minLength}
                maxLength={maxLength}
            />
        </>
    );
};

Textarea.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as TextareaType;
    const { summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);
    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={value} />;
};

Textarea.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Textarea.getValue(formValues, elementId);
    const valueStr = Textarea.getValueHuman(formValues, elementId);

    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('textarea', elementId, label, valueStr, valueId);
};

Textarea.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Textarea.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Textarea.getValue(formValues, elementId);
    if (value === undefined) {
        return '';
    }
    return value;
};

Textarea.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const value = Textarea.getValue(formValues, elementId);

    return value;
};

// This returns the component's isValid status
Textarea.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    return getIsValid(formConfig, formValues, elementId);
};

Textarea.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasHadFocusAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }
    if (!fieldHasHadFocusAtLeastOnce) {
        return undefined;
    }

    // const value = Textarea.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    // if (value === '' || value === undefined) {
    //     return undefined;
    // }

    const isValid = Textarea.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Textarea;
